import { Accessor, Setter, createSignal } from "solid-js";

import { watchAudioClock } from "./audioClock";

import {
  LocalTrackPublication,
  Room,
  Track,
  TrackPublishOptions,
} from "livekit-client";

/**
 * Konkord desktop (Windows 10 2004+): the sound of single programs in a
 * stream, one track per program, so viewers can mix them. The call itself
 * (Konkord) never goes in.
 *
 * - sharing a window: the sound of that window's program
 * - sharing a screen: every program playing sound (checked every 2 s); the
 *   streamer can leave programs out (remembered by name)
 *
 * The desktop app captures each program (Windows process loopback) and sends
 * 16 bit stereo 48 kHz PCM through a MessagePort; an AudioWorklet turns it
 * into a MediaStreamTrack for LiveKit.
 */

/**
 * Stream sound tracks of single programs are named "app:<program>"
 */
const APP_TRACK_PREFIX = "app:";

const SAMPLE_RATE = 48_000;
const POLL_MS = 2000;

/**
 * A program that stopped playing keeps its track this long (pauses between
 * songs; a program beeping once a minute had its track replaced every minute)
 */
const IDLE_MS = 120_000;
const PORT_TIMEOUT_MS = 5000;
const SUPPORT_WAIT_MS = 1500;
const PORT_MESSAGE = "konkord-app-audio-port";

/**
 * The program of a stream sound track, if it carries a single program
 */
export function appOfTrack(trackName: string | undefined) {
  return trackName?.startsWith(APP_TRACK_PREFIX)
    ? trackName.slice(APP_TRACK_PREFIX.length)
    : undefined;
}

/**
 * Key under which settings for a program are remembered
 */
export function appKey(label: string) {
  return label.toLowerCase();
}

const KNOWN_NAMES: Record<string, string> = {
  chrome: "Chrome",
  msedge: "Edge",
  firefox: "Firefox",
  opera: "Opera",
  opera_gx: "Opera GX",
  brave: "Brave",
  spotify: "Spotify",
  vlc: "VLC",
  steam: "Steam",
  steamwebhelper: "Steam",
  discord: "Discord",
  obs64: "OBS",
  wmplayer: "Windows Media Player",
  "microsoft.media.player": "Media Player",
  applemusic: "Apple Music",
  itunes: "iTunes",
  potplayermini64: "PotPlayer",
};

function labelOf(processName: string | null | undefined) {
  const name = (processName ?? "").trim();
  if (!name) return "Programa";
  const label =
    KNOWN_NAMES[name.toLowerCase()] ??
    name.charAt(0).toUpperCase() + name.slice(1);
  return label.slice(0, 40);
}

/**
 * Plays the PCM chunks it receives (through a transferred MessagePort) with
 * 20 ms of buffer; drops what piles up past 150 ms (clock drift, hiccups)
 */
const PROCESSOR = "konkord-app-audio";
const PROCESSOR_CODE = `
class KonkordAppAudio extends AudioWorkletProcessor {
  constructor() {
    super();
    this.size = 24000;
    this.left = new Float32Array(this.size);
    this.right = new Float32Array(this.size);
    this.read = 0;
    this.count = 0;
    this.playing = false;
    this.alive = true;
    this.port.onmessage = (e) => {
      const d = e.data;
      if (d && d.type === "source" && d.port) d.port.onmessage = (m) => this.push(m.data);
      else if (d && d.type === "stop") this.alive = false;
      else this.push(d);
    };
  }
  push(data) {
    let pcm;
    if (data instanceof ArrayBuffer) pcm = new Int16Array(data, 0, data.byteLength >> 1);
    else if (ArrayBuffer.isView(data)) {
      const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength).slice();
      pcm = new Int16Array(bytes.buffer, 0, bytes.byteLength >> 1);
    } else return;
    const frames = pcm.length >> 1;
    for (let i = 0; i < frames; i++) {
      if (this.count === this.size) {
        this.read = (this.read + 1) % this.size;
        this.count--;
      }
      const w = (this.read + this.count) % this.size;
      this.left[w] = pcm[2 * i] / 32768;
      this.right[w] = pcm[2 * i + 1] / 32768;
      this.count++;
    }
    if (this.count > 7200) {
      const skip = this.count - 1920;
      this.read = (this.read + skip) % this.size;
      this.count -= skip;
    }
  }
  process(inputs, outputs) {
    const out = outputs[0];
    const l = out[0];
    const r = out[1] || out[0];
    const n = l.length;
    if (!this.playing && this.count >= 960) this.playing = true;
    let i = 0;
    if (this.playing) {
      for (; i < n && this.count > 0; i++) {
        l[i] = this.left[this.read];
        r[i] = this.right[this.read];
        this.read = (this.read + 1) % this.size;
        this.count--;
      }
      if (i < n) this.playing = false;
    }
    for (; i < n; i++) {
      l[i] = 0;
      r[i] = 0;
    }
    return this.alive;
  }
}
registerProcessor("${PROCESSOR}", KonkordAppAudio);
`;

/**
 * Ports from the desktop app arrive as window messages (they can't cross the
 * context bridge)
 */
const portWaiters = new Map<string, (port?: MessagePort) => void>();
let listening = false;

function waitForPort(id: string): Promise<MessagePort | undefined> {
  if (!listening) {
    listening = true;
    window.addEventListener("message", (event) => {
      if (event.source !== window || event.data?.type !== PORT_MESSAGE) return;
      const port = event.ports[0];
      const deliver = portWaiters.get(event.data.id);
      portWaiters.delete(event.data.id);
      if (deliver && port) deliver(port);
      else port?.close();
    });
  }

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      portWaiters.delete(id);
      resolve(undefined);
    }, PORT_TIMEOUT_MS);
    portWaiters.set(id, (port) => {
      clearTimeout(timer);
      resolve(port);
    });
  });
}

/**
 * The desktop app refused a capture: stop waiting for its port
 */
function failPort(id: string) {
  const deliver = portWaiters.get(id);
  portWaiters.delete(id);
  deliver?.();
}

let supported: Promise<boolean> | undefined;

/**
 * Whether this desktop app can send programs' sound on their own. Starting a
 * share doesn't wait long for the first check (the click must stay "fresh"
 * for the browser): a slow answer means the old mixed sound this time.
 */
export function appAudioSupported(): Promise<boolean> {
  const native = window.native?.appAudio;
  if (!native) return Promise.resolve(false);
  supported ??= native.supported().catch(() => false);
  return Promise.race([
    supported,
    new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(false), SUPPORT_WAIT_MS),
    ),
  ]);
}

/**
 * Capture ids are unique per page load (old captures may still report back)
 */
const PAGE = Date.now().toString(36);

/**
 * Stands for "everything except Konkord" in the programs list
 */
const WHOLE_PC = 0;

type Program = {
  pid: number;
  label: string;
  key: string;
  seenAt: number;
  playing: boolean;
};

type Capture = {
  id: string;
  room: Room;
  node: AudioWorkletNode;
  track: MediaStreamTrack;
  publication?: LocalTrackPublication;
};

/**
 * A program in our stream, for the stream menu
 */
export type StreamApp = {
  key: string;
  label: string;
  on: boolean;
  playing: boolean;
};

export class AppAudio {
  /**
   * "window": the shared window's program · "screen": every program playing sound
   */
  readonly mode: Accessor<"window" | "screen" | undefined>;
  #setMode: Setter<"window" | "screen" | undefined>;
  readonly apps: Accessor<StreamApp[]>;
  #setApps: Setter<StreamApp[]>;

  #isOff: (key: string) => boolean;
  #room?: Room;
  #options: TrackPublishOptions = {};
  #programs = new Map<number, Program>();
  #captures = new Map<number, Capture>();
  #timer?: ReturnType<typeof setInterval>;
  #gen = 0;
  #queue: Promise<void> = Promise.resolve();
  #context?: AudioContext;
  /** Konkord: stops watching the context's clock (audioClock.ts) */
  #stopClock?: () => void;
  #module?: Promise<void>;
  #count = 0;
  #wholePc = false;

  constructor(isOff: (key: string) => boolean) {
    this.#isOff = isOff;
    const [mode, setMode] = createSignal<"window" | "screen">();
    this.mode = mode;
    this.#setMode = setMode;
    const [apps, setApps] = createSignal<StreamApp[]>([]);
    this.apps = apps;
    this.#setApps = setApps;

    // a capture that stops by itself (or is refused): fall back to the PC's sound
    window.native?.appAudio?.onEnded?.((id) => {
      failPort(id);
      for (const [pid, capture] of this.#captures) {
        if (capture.id !== id) continue;
        if (pid === WHOLE_PC) {
          console.error("[konkord] the PC's sound stopped");
          return;
        }
        console.error("[konkord] a program's sound stopped, sending the PC's");
        this.#useWholePc(this.#gen);
        return;
      }
    });
  }

  /**
   * Send the sound for the source picked in the desktop app's picker
   * ("screen:…" or "window:<hwnd>:…")
   */
  async start(room: Room, sourceId: string, options: TrackPublishOptions) {
    const stopped = this.stop();
    // a stop() arriving while the old captures are released wins
    const gen = this.#gen;
    await stopped;
    const native = window.native?.appAudio;
    if (!native || gen !== this.#gen) return;

    this.#room = room;
    this.#options = options;

    const hwnd = /^window:(\d+):/.exec(sourceId)?.[1];
    if (hwnd) {
      this.#setMode("window");
      const owner = await native.windowProcess(Number(hwnd)).catch(() => null);
      if (gen !== this.#gen) return;
      // Store apps' windows belong to a frame host, not to the app playing
      if (!owner || /^applicationframehost$/i.test(owner.name ?? "")) {
        this.#useWholePc(gen);
        return;
      }
      const label = labelOf(owner.name);
      this.#programs.set(owner.pid, {
        pid: owner.pid,
        label,
        key: appKey(label),
        // the window's program stays for the whole stream
        seenAt: Infinity,
        playing: false,
      });
      this.#enqueue(gen);
    } else {
      this.#setMode("screen");
      void this.#poll(gen);
      this.#timer = setInterval(() => void this.#poll(gen), POLL_MS);
    }
  }

  /**
   * Stop sending every program's sound
   */
  async stop() {
    this.#gen++;
    clearInterval(this.#timer);
    this.#timer = undefined;
    this.#programs.clear();
    this.#wholePc = false;
    this.#setMode(undefined);
    this.#setApps([]);

    const captures = [...this.#captures.values()];
    this.#captures.clear();
    await Promise.all(captures.map((capture) => this.#release(capture)));
    if (!this.#captures.size && this.#context?.state === "running") {
      await this.#context.suspend().catch(() => {});
    }
  }

  /**
   * Apply programs switched on / off in the settings
   */
  refresh() {
    if (this.mode()) this.#enqueue(this.#gen);
  }

  /**
   * Programs can't be captured one by one: send everything except Konkord
   */
  #useWholePc(gen: number) {
    if (gen !== this.#gen || this.#wholePc) return;
    this.#wholePc = true;
    clearInterval(this.#timer);
    this.#timer = undefined;
    this.#programs.clear();
    this.#programs.set(WHOLE_PC, {
      pid: WHOLE_PC,
      label: "Som do PC",
      key: appKey("Som do PC"),
      seenAt: Infinity,
      playing: false,
    });
    this.#enqueue(gen);
  }

  async #poll(gen: number) {
    const sessions =
      (await window.native?.appAudio?.sessions().catch(() => [])) ?? [];
    if (gen !== this.#gen || this.#wholePc) return;

    // capturing a program brings the programs it started along (a launcher
    // and its game): a program whose own child plays is left to the child
    const covered = new Set<number>();
    for (const session of sessions) {
      if (session.active) session.parents?.forEach((pid) => covered.add(pid));
    }

    const now = Date.now();
    const playing = new Set<number>();
    for (const session of sessions) {
      if (!session.active || covered.has(session.pid)) continue;
      playing.add(session.pid);
      const known = this.#programs.get(session.pid);
      if (known) {
        known.seenAt = now;
        continue;
      }
      const name = labelOf(session.name);
      this.#programs.set(session.pid, {
        pid: session.pid,
        label: this.#uniqueLabel(name),
        key: appKey(name),
        seenAt: now,
        playing: true,
      });
    }

    for (const [pid, program] of this.#programs) {
      program.playing = playing.has(pid);
      // quiet for a while (or closed): its track goes until it plays again
      if (now - program.seenAt > IDLE_MS || covered.has(pid)) {
        this.#programs.delete(pid);
      }
    }
    this.#enqueue(gen);
  }

  /**
   * Two programs with the same name (two games, two players) get a number
   */
  #uniqueLabel(label: string) {
    const taken = new Set([...this.#programs.values()].map((p) => p.label));
    if (!taken.has(label)) return label;
    let n = 2;
    while (taken.has(`${label} ${n}`)) n++;
    return `${label} ${n}`;
  }

  #enqueue(gen: number) {
    this.#queue = this.#queue
      .then(() => this.#sync(gen))
      .catch((err) => console.error("[konkord] program sound", err));
  }

  /**
   * Capture the programs that should be in the stream, release the rest
   */
  async #sync(gen: number) {
    if (gen !== this.#gen) return;

    for (const [pid, capture] of [...this.#captures]) {
      const program = this.#programs.get(pid);
      if (program && !this.#isOff(program.key)) continue;
      this.#captures.delete(pid);
      await this.#release(capture);
    }

    for (const program of [...this.#programs.values()]) {
      if (gen !== this.#gen) return;
      if (this.#captures.has(program.pid) || this.#isOff(program.key)) continue;
      await this.#add(program, gen);
    }

    if (gen !== this.#gen) return;
    this.#setApps(
      [...this.#programs.values()]
        .map((program) => ({
          key: program.key,
          label: program.label,
          on: !this.#isOff(program.key),
          playing: program.playing,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    );
  }

  async #add(program: Program, gen: number) {
    const native = window.native?.appAudio;
    const room = this.#room;
    if (!native || !room) return;

    const context = await this.#audioContext();
    if (gen !== this.#gen) return;

    const id = `konkord-app-${PAGE}-${++this.#count}`;
    const portReady = waitForPort(id);
    if (program.pid === WHOLE_PC) native.start(id, "exclude");
    else native.start(id, "include", program.pid);
    const source = await portReady;
    if (!source || gen !== this.#gen) {
      native.stop(id);
      source?.close();
      if (!source && program.pid !== WHOLE_PC) this.#useWholePc(gen);
      return;
    }

    const node = new AudioWorkletNode(context, PROCESSOR, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    try {
      // straight into the audio thread
      node.port.postMessage({ type: "source", port: source }, [source]);
    } catch {
      source.onmessage = (event) => node.port.postMessage(event.data);
    }
    const destination = context.createMediaStreamDestination();
    destination.channelCount = 2;
    node.connect(destination);

    const capture: Capture = {
      id,
      room,
      node,
      track: destination.stream.getAudioTracks()[0],
    };
    this.#captures.set(program.pid, capture);

    try {
      capture.publication = await room.localParticipant.publishTrack(
        capture.track,
        {
          ...this.#options,
          source: Track.Source.ScreenShareAudio,
          name: APP_TRACK_PREFIX + program.label,
        },
      );
    } catch (err) {
      console.error("[konkord] couldn't send", program.label, err);
      if (this.#captures.get(program.pid) === capture) {
        this.#captures.delete(program.pid);
      }
      await this.#release(capture);
      return;
    }

    // stopped while publishing
    if (gen !== this.#gen || this.#captures.get(program.pid) !== capture) {
      if (this.#captures.get(program.pid) === capture) {
        this.#captures.delete(program.pid);
      }
      await this.#release(capture);
    }
  }

  async #release(capture: Capture) {
    window.native?.appAudio?.stop(capture.id);
    capture.node.port.postMessage({ type: "stop" });
    capture.node.disconnect();
    if (capture.publication) {
      // by the MediaStreamTrack: a full reconnect republishes it under a new
      // publication, the old one no longer has a track
      await capture.room.localParticipant
        .unpublishTrack(capture.track)
        .catch(() => {});
    }
    capture.track.stop();
  }

  async #audioContext() {
    if (!this.#context || this.#context.state === "closed") {
      this.#context = new AudioContext({
        sampleRate: SAMPLE_RATE,
        latencyHint: "interactive",
      });
      // Konkord: on the sound card's clock, never a slower timer (the
      // program sound broke up with the microphone, see audioClock.ts)
      this.#stopClock?.();
      this.#stopClock = watchAudioClock(this.#context, (slow) =>
        console.warn(
          `[konkord] stream sound ${slow ? "lagging behind real time" : "back to real time"}`,
        ),
      );
      const url = URL.createObjectURL(
        new Blob([PROCESSOR_CODE], { type: "application/javascript" }),
      );
      this.#module = this.#context.audioWorklet
        .addModule(url)
        .finally(() => URL.revokeObjectURL(url));
    }
    await this.#module;
    if (this.#context.state !== "running") {
      await this.#context.resume().catch(() => {});
    }
    return this.#context;
  }
}
